import { ReactNode, useContext, useMemo } from "react";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { pluralizeWithCount } from "@common/helpers/string";
import { financialDocumentsForDraftSelection } from "./accrualImporterDraftGroups";
import { AccrualImporterContext } from "./accrualImporter.context";
import {
    confirmableDocumentsForSelection,
    isAccrualRowEligibleForBulkConfirm,
} from "./accrualDraftBulkEligibility";
import {
    AccrualImporterDocumentRow,
    AccrualImporterViewRow,
} from "./accrualImporter.types";

interface Props {
    rowsReadyForImport: AccrualImporterDocumentRow[];
    selectedRows?: AccrualImporterViewRow[];
    confirmDocuments: (financialDocumentIds: number[]) => void;
    isConfirming: boolean;
    fallback?: ReactNode;
}

export const AccrualImporterReviewButton: React.FC<Props> = ({
    rowsReadyForImport,
    selectedRows,
    confirmDocuments,
    isConfirming,
    fallback = null,
}) => {
    const { mode } = useContext(AccrualImporterContext);

    const hasSelection = (selectedRows?.length ?? 0) > 0;
    const reviewDocuments = useMemo(
        () =>
            hasSelection
                ? confirmableDocumentsForSelection(selectedRows ?? [])
                : financialDocumentsForDraftSelection(
                      rowsReadyForImport.filter(
                          isAccrualRowEligibleForBulkConfirm,
                      ),
                  ),
        [hasSelection, rowsReadyForImport, selectedRows],
    );
    const reviewCount = reviewDocuments.length;
    const itemLabel = mode === "invoices" ? "invoice" : "bill";

    if (reviewCount === 0) {
        return fallback;
    }

    const reviewLabel = `Confirm ${pluralizeWithCount(itemLabel, reviewCount)}`;

    return (
        <ButtonWithLoader
            variant="primary"
            size="md"
            onClick={() =>
                confirmDocuments(reviewDocuments.map((document) => document.id))
            }
            loading={isConfirming}
            hotKey="mod+enter"
            hotKeyDisplayPosition="inline"
            hotKeyOptions={{
                description: reviewLabel,
            }}
        >
            {reviewLabel}
        </ButtonWithLoader>
    );
};
